import { z } from "zod";
/**
 * Every kind of source document the shared extraction capability
 * (`ARCHITECTURE.md` "Non-Planner AI/document extraction": "S3 upload ->
 * SQS/Step Functions -> Lambda preprocessing -> Bedrock Nova structured JSON
 * extraction -> schema validation -> Aurora draft -> authorized human
 * review") knows how to map to a target field schema. `HORSE_PASSPORT`/
 * `HORSE_NAME_PLATE` back Horse Details' add-horse scan
 * (`docs/products/stable.md` "Adding a horse"). `VACCINATION_STAMP` backs
 * Vaccinations' stamped-passport-page scan (`docs/products/stable.md`
 * "Vaccinations (Sealed)"). `BILL_RECEIPT` backs Accounting's bill/receipt
 * capture (`docs/products/stable.md` "Accounting") — the first kind that
 * accepts a PDF as well as a photo (ADR 0017). Every port method below is
 * generic over `ExtractionKind`, so each addition was a new enum literal
 * plus a new field-mapping module, not a port redesign.
 */
export const EXTRACTION_KINDS = [
    "HORSE_PASSPORT",
    "HORSE_NAME_PLATE",
    "VACCINATION_STAMP",
    "BILL_RECEIPT",
];
export const ExtractionKindSchema = z.enum(EXTRACTION_KINDS);
/** Every non-`BILL_RECEIPT` extraction kind is a single photograph, never a
 * multi-page PDF (unlike Vet/Farrier Records' document attachments) —
 * mirrors `docs/products/stable.md`'s wording for both the horse-add scan
 * ("a passport page or name-plate photo") and Vaccinations' scan ("a vet
 * photographs the stamped passport page"). `BILL_RECEIPT` additionally
 * accepts a PDF (see {@link EXTRACTION_PDF_CONTENT_TYPES}, ADR 0017). */
export const EXTRACTION_PHOTO_CONTENT_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
];
export const EXTRACTION_PHOTO_MAX_SIZE_BYTES = 10 * 1024 * 1024;
export const EXTRACTION_PHOTO_FILENAME_MAX_LENGTH = 180;
const EXTRACTION_PHOTO_EXTENSIONS = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
};
export function matchesExtractionPhotoExtension(contentType, filename) {
    const extensions = EXTRACTION_PHOTO_EXTENSIONS[contentType];
    if (extensions === undefined)
        return false;
    const lower = filename.toLowerCase();
    return extensions.some((extension) => lower.endsWith(extension));
}
/**
 * `BILL_RECEIPT`-only PDF upload support (ADR 0017: "the first
 * `ExtractionKind` that accepts a PDF upload alongside a photo"). Capped at
 * 4.5 MB, matching Bedrock's documented general per-document content-block
 * size limit exactly rather than relying on an unconfirmed Nova-specific
 * exception (ADR 0017 "Decision") — deliberately smaller than
 * {@link EXTRACTION_PHOTO_MAX_SIZE_BYTES}.
 */
export const EXTRACTION_PDF_CONTENT_TYPES = ["application/pdf"];
export const EXTRACTION_PDF_MAX_SIZE_BYTES = 4_500_000;
export function isExtractionPdfContentType(contentType) {
    return EXTRACTION_PDF_CONTENT_TYPES.includes(contentType);
}
export function matchesExtractionPdfExtension(contentType, filename) {
    if (!isExtractionPdfContentType(contentType))
        return false;
    return filename.toLowerCase().endsWith(".pdf");
}
/** The kinds that accept a PDF upload in addition to a photo — today, only
 * `BILL_RECEIPT` (ADR 0017). */
export const EXTRACTION_PDF_ELIGIBLE_KINDS = ["BILL_RECEIPT"];
export function acceptsExtractionPdf(kind) {
    return EXTRACTION_PDF_ELIGIBLE_KINDS.includes(kind);
}
